import { useEffect, useRef, useState } from "react";
import { X, Pin, Upload, Plus, Database, Trash2 } from "@semo/icons";
import type { MajorWork, MidWork } from "../types";
import { formatWon, itemAmount, majorWorkTotal, midWorkTotal } from "../calc";
import { DbSearchModal } from "./DbSearchModal";
import { DeltaBadge } from "./DeltaBadge";

/** 품목에 채울 수 있는 필드 (id 제외) */
export interface NewItemInput {
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  custom?: boolean; // 직접 추가한 항목이면 품명/단위 편집 허용
}

type ItemPatch = Partial<NewItemInput>;

interface DetailPanelProps {
  title: string; // "직접재료비"
  subtotal: number; // 소계 금액
  majors: MajorWork[]; // 대공사 리스트 (비어있으면 빈 상태)
  onClose: () => void;
  height: number; // 패널 높이(px)
  onResize: (clientY: number) => void; // 드래그 시 포인터 Y 전달
  /** 품목 필드 편집 (없으면 보기 전용) */
  onEditItem?: (
    majorCode: string,
    midCode: string,
    itemId: string,
    patch: ItemPatch,
  ) => void;
  /** 품목 추가 (직접 추가 / DB / 엑셀 공용) */
  onAddItems?: (
    majorCode: string,
    midCode: string,
    items: NewItemInput[],
  ) => void;
  /** 품목 삭제 */
  onDeleteItem?: (majorCode: string, midCode: string, itemId: string) => void;
  /** 엑셀 파일 업로드 */
  onUploadExcel?: (file: File) => void;
  /** DB 검색 종류 (자재 / 시공·인건) */
  dbKind?: "material" | "labor";
  /** 품목 필드 수정됨(저장 전) 여부 */
  isItemDirty?: (
    majorCode: string,
    midCode: string,
    itemId: string,
    field: "quantity" | "unitPrice",
  ) => boolean;
}

/** 동시에 보이는 대공사 컬럼 최대 개수 (가로 분할 한도) */
const MAX_COLUMNS = 2;

/** 패널 높이 조절용 드래그 핸들 (split-pane 스타일) */
function DragHandle({ onResize }: { onResize: (clientY: number) => void }) {
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (dragging.current) onResize(e.clientY);
    };
    const up = () => {
      dragging.current = false;
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [onResize]);

  return (
    <div
      onPointerDown={() => {
        dragging.current = true;
        document.body.style.userSelect = "none";
      }}
      className="group flex h-3.5 shrink-0 cursor-row-resize items-center justify-center bg-primary transition-colors hover:bg-primary-hover"
      role="separator"
      aria-label="패널 높이 조절"
    >
      <div className="h-1 w-12 rounded-full bg-white/50 transition-colors group-hover:bg-white/80" />
    </div>
  );
}

/** 엑셀 스타일 탭 (대공사/소공사 공용) — 본문 클릭 = 보기, 핀 아이콘 = 고정 토글 */
function ExcelTab({
  label,
  amount,
  active,
  pinned,
  onSelect,
  onTogglePin,
}: {
  label: string;
  amount: number;
  active: boolean;
  pinned: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={active}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`group -mb-px flex shrink-0 cursor-pointer items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm transition-colors ${
        active
          ? "border-b-primary font-semibold text-primary"
          : "border-b-transparent text-slate-500 hover:text-slate-700"
      } ${pinned ? "bg-primary/5" : ""}`}
    >
      <span className="whitespace-nowrap">{label}</span>
      <span className="whitespace-nowrap text-xs font-normal text-slate-400">
        {formatWon(amount)}
      </span>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
        className="ml-0.5 rounded p-0.5 hover:bg-slate-200/60"
        aria-label={pinned ? "핀 해제" : "핀 고정"}
      >
        <Pin
          className={`h-3.5 w-3.5 transition-colors ${
            pinned
              ? "fill-primary text-primary"
              : "text-slate-300 group-hover:text-slate-400"
          }`}
        />
      </button>
    </div>
  );
}

/** 클릭/포커스 시 편집되는 숫자 셀 (비포커스 시 콤마 표시) */
function EditableNumber({
  value,
  onChange,
  dirty,
}: {
  value: number;
  onChange: (v: number) => void;
  dirty?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(String(value));

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  return (
    <span className="relative block">
      <input
        type="text"
        inputMode="decimal"
        value={focused ? text : formatWon(value)}
        onFocus={() => {
          setFocused(true);
          setText(String(value));
        }}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, "");
          setText(raw);
          onChange(raw === "" ? 0 : parseFloat(raw) || 0);
        }}
        className="w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums text-slate-700 transition-colors hover:border-slate-300 focus:border-primary focus:bg-white focus:text-slate-900 focus:outline-none"
      />
      {dirty && (
        <span
          className="pointer-events-none absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-amber-400"
          title="수정됨(저장 전)"
        />
      )}
    </span>
  );
}

/** 편집 가능한 텍스트 셀 (품명/단위) */
function EditableText({
  value,
  onChange,
  align = "left",
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  align?: "left" | "center";
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-slate-800 transition-colors placeholder:text-slate-300 hover:border-slate-300 focus:border-primary focus:bg-white focus:outline-none ${
        align === "center" ? "text-center" : "text-left"
      }`}
    />
  );
}

/** 금액 셀 — 값이 바뀌면 변동량을 잠깐 보여주고 사라짐(transient) */
function TransientAmount({ amount }: { amount: number }) {
  const prev = useRef(amount);
  const [delta, setDelta] = useState(0);

  useEffect(() => {
    if (amount === prev.current) return undefined;
    setDelta(amount - prev.current);
    prev.current = amount;
    const t = setTimeout(() => setDelta(0), 2500);
    return () => clearTimeout(t);
  }, [amount]);

  return (
    <>
      {formatWon(amount)}
      <DeltaBadge value={delta} />
    </>
  );
}

/** 품목 필드 변경 콜백 */
type EditItemFn = (itemId: string, patch: ItemPatch) => void;

/** 품목 필드 수정됨 여부 */
type ItemDirtyFn = (itemId: string, field: "quantity" | "unitPrice") => boolean;

/** 소공사 품목 표 (스택으로 쌓임) */
function MidWorkTable({
  mid,
  pinned,
  onTogglePin,
  onEditItem,
  onDeleteItem,
  isDirty,
}: {
  mid: MidWork;
  pinned: boolean;
  onTogglePin: () => void;
  onEditItem?: EditItemFn;
  onDeleteItem?: (itemId: string) => void;
  isDirty?: ItemDirtyFn;
}) {
  const editable = Boolean(onEditItem);
  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-slate-600">
        <button
          type="button"
          onClick={onTogglePin}
          className="rounded p-0.5 hover:bg-slate-200/60"
          aria-label={pinned ? "핀 해제" : "핀 고정"}
        >
          <Pin
            className={`h-3 w-3 ${pinned ? "fill-primary text-primary" : "text-slate-300"}`}
          />
        </button>
        {mid.code} {mid.name}
        <span className="text-slate-400">
          — {formatWon(midWorkTotal(mid))}원
        </span>
      </div>
      {mid.items.length === 0 ? (
        <div className="rounded border border-dashed border-slate-200 bg-slate-50/50 py-4 text-center text-xs text-slate-400">
          등록된 품목이 없습니다.
        </div>
      ) : (
        <table className="w-full table-fixed border-collapse text-sm [&_td]:border [&_td]:border-slate-200 [&_th]:border [&_th]:border-slate-200">
          <colgroup>
            <col />
            {/* 품명: 가변 (남는 공간) */}
            <col className="w-12" /> {/* 단위 */}
            <col className="w-16" /> {/* 수량 */}
            <col className="w-24" /> {/* 단가 */}
            <col className="w-24" /> {/* 금액 */}
            {editable && <col className="w-8" />} {/* 삭제 */}
          </colgroup>
          <thead>
            <tr className="bg-slate-100 text-xs text-slate-500">
              <th className="px-2 py-1.5 text-left font-semibold">품명</th>
              <th className="px-2 py-1.5 text-center font-semibold">단위</th>
              <th className="px-2 py-1.5 text-right font-semibold">수량</th>
              <th className="px-2 py-1.5 text-right font-semibold">단가</th>
              <th className="px-2 py-1.5 text-right font-semibold">금액</th>
              {editable && <th className="py-1.5" />}
            </tr>
          </thead>
          <tbody>
            {mid.items.map((item) => (
              <tr key={item.id} className="group/row">
                <td className="break-words px-2 py-1 text-slate-800">
                  {onEditItem && item.custom ? (
                    <EditableText
                      value={item.name}
                      onChange={(v) => onEditItem(item.id, { name: v })}
                      placeholder="품명 입력"
                    />
                  ) : (
                    item.name
                  )}
                </td>
                <td className="px-1 py-1 text-center text-slate-600">
                  {onEditItem && item.custom ? (
                    <EditableText
                      value={item.unit}
                      onChange={(v) => onEditItem(item.id, { unit: v })}
                      align="center"
                      placeholder="단위"
                    />
                  ) : (
                    item.unit
                  )}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                  {onEditItem ? (
                    <EditableNumber
                      value={item.quantity}
                      onChange={(v) => onEditItem(item.id, { quantity: v })}
                      dirty={isDirty?.(item.id, "quantity")}
                    />
                  ) : (
                    formatWon(item.quantity)
                  )}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-slate-600">
                  {onEditItem ? (
                    <EditableNumber
                      value={item.unitPrice}
                      onChange={(v) => onEditItem(item.id, { unitPrice: v })}
                      dirty={isDirty?.(item.id, "unitPrice")}
                    />
                  ) : (
                    formatWon(item.unitPrice)
                  )}
                </td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-900">
                  <TransientAmount amount={itemAmount(item)} />
                </td>
                {editable && (
                  <td className="py-1 text-center">
                    <button
                      type="button"
                      onClick={() => onDeleteItem?.(item.id)}
                      className="rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-500 group-hover/row:opacity-100"
                      aria-label="품목 삭제"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** 소공사 보기 상태: 현재 보는 탭 + 핀 고정된 탭들 */
interface SubState {
  active: string;
  pinned: string[];
}

/** 대공사 컬럼 — 소공사 탭바 + 스택된 품목 표 (세로 스크롤) */
function MajorColumn({
  major,
  sub,
  showHeader,
  onSelectSub,
  onTogglePinSub,
  onEditItem,
  onDeleteItem,
  isItemDirty,
}: {
  major: MajorWork;
  sub: SubState;
  showHeader: boolean; // 핀/2분할일 때만 대공사 헤더 표시
  onSelectSub: (code: string) => void;
  onTogglePinSub: (code: string) => void;
  onEditItem?: (midCode: string, itemId: string, patch: ItemPatch) => void;
  onDeleteItem?: (midCode: string, itemId: string) => void;
  isItemDirty?: (
    midCode: string,
    itemId: string,
    field: "quantity" | "unitPrice",
  ) => boolean;
}) {
  // 화면에 쌓일 소공사 = 핀된 것들 ∪ 현재 보는 것 (원래 순서 유지)
  const shown = major.midWorks.filter(
    (m) => m.code === sub.active || sub.pinned.includes(m.code),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
      {/* 컬럼 헤더 (핀/2분할일 때만) */}
      {showHeader && (
        <div className="flex shrink-0 items-center gap-1.5 border-b border-slate-100 px-3 py-1.5 text-xs font-medium text-primary/80">
          {major.code} {major.name}
          <span className="text-2xs font-normal text-slate-400">
            소계 {formatWon(majorWorkTotal(major))}원
          </span>
        </div>
      )}

      {/* 소공사 탭바 (가로 스크롤) */}
      <div className="flex shrink-0 flex-wrap [&>*+*]:border-l [&>*+*]:border-l-slate-200 border-b border-slate-200 bg-white px-3">
        {major.midWorks.map((mid) => (
          <ExcelTab
            key={mid.code}
            label={`${mid.code} ${mid.name}`}
            amount={midWorkTotal(mid)}
            active={sub.active === mid.code}
            pinned={sub.pinned.includes(mid.code)}
            onSelect={() => onSelectSub(mid.code)}
            onTogglePin={() => onTogglePinSub(mid.code)}
          />
        ))}
      </div>

      {/* 품목 표 (세로 스크롤) */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
        {shown.map((mid) => (
          <MidWorkTable
            key={mid.code}
            mid={mid}
            pinned={sub.pinned.includes(mid.code)}
            onTogglePin={() => onTogglePinSub(mid.code)}
            onEditItem={
              onEditItem
                ? (itemId, patch) => onEditItem(mid.code, itemId, patch)
                : undefined
            }
            onDeleteItem={
              onDeleteItem
                ? (itemId) => onDeleteItem(mid.code, itemId)
                : undefined
            }
            isDirty={
              isItemDirty
                ? (itemId, field) => isItemDirty(mid.code, itemId, field)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

export function DetailPanel({
  title,
  subtotal,
  majors,
  onClose,
  height,
  onResize,
  onEditItem,
  onAddItems,
  onDeleteItem,
  onUploadExcel,
  dbKind = "material",
  isItemDirty,
}: DetailPanelProps) {
  const hasData = majors.length > 0;

  // 현재 보고 있는 대공사(미핀 시 단일 표시) / 핀 고정된 대공사
  const [activeMajor, setActiveMajor] = useState<string>(
    () => majors[0]?.code ?? "",
  );
  const [pinnedMajors, setPinnedMajors] = useState<string[]>([]);
  // 대공사별 소공사 보기 상태 (key: 대공사 코드)
  const [subState, setSubState] = useState<Map<string, SubState>>(new Map());
  // DB 검색 팝업 / 엑셀 파일 입력
  const [dbModalOpen, setDbModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultSub = (major: MajorWork): SubState => ({
    active: major.midWorks[0]?.code ?? "",
    pinned: [],
  });

  // 클릭 = 보기 (핀은 건드리지 않음)
  const selectMajor = (code: string) => setActiveMajor(code);

  // 핀 토글 (최대 2개까지만 핀 가능). 핀하면 그 대공사를 보기로도 설정
  const togglePinMajor = (code: string) => {
    setPinnedMajors((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      if (prev.length >= MAX_COLUMNS) return prev; // 이미 2개 핀이면 무시
      return [...prev, code];
    });
    setActiveMajor(code);
  };

  const selectSub = (major: MajorWork, subCode: string) => {
    setSubState((prev) => {
      const next = new Map(prev);
      const cur = next.get(major.code) ?? defaultSub(major);
      next.set(major.code, { ...cur, active: subCode });
      return next;
    });
  };

  const togglePinSub = (major: MajorWork, subCode: string) => {
    setSubState((prev) => {
      const next = new Map(prev);
      const cur = next.get(major.code) ?? defaultSub(major);
      const isPinning = !cur.pinned.includes(subCode);
      const pinned = isPinning
        ? [...cur.pinned, subCode]
        : cur.pinned.filter((c) => c !== subCode);
      // 핀하면 그 소공사를 보기로도 설정
      next.set(major.code, {
        active: isPinning ? subCode : cur.active,
        pinned,
      });
      return next;
    });
  };

  // 표시할 대공사 = 핀 ∪ (현재 보기). 최대 2개, 핀 우선, 대공사 순서로 정렬
  const shownCodes = new Set(pinnedMajors);
  if (shownCodes.size < MAX_COLUMNS && activeMajor) shownCodes.add(activeMajor);
  const columns = majors.filter((m) => shownCodes.has(m.code));

  // 품목 추가 대상 = 현재 활성 대공사 + 그 대공사의 활성 소공사
  const targetMajor = columns.find((m) => m.code === activeMajor) ?? columns[0];
  const targetMid =
    targetMajor &&
    (subState.get(targetMajor.code)?.active ??
      targetMajor.midWorks[0]?.code ??
      "");

  const handleAddBlankItem = () => {
    if (targetMajor && targetMid && onAddItems) {
      onAddItems(targetMajor.code, targetMid, [
        { name: "", unit: "", quantity: 0, unitPrice: 0, custom: true },
      ]);
    }
  };

  const handleAddDbItems = (items: NewItemInput[]) => {
    if (targetMajor && targetMid && onAddItems) {
      onAddItems(targetMajor.code, targetMid, items);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onUploadExcel?.(file);
    e.target.value = ""; // 같은 파일 재선택 가능하도록 리셋
  };

  return (
    <div
      style={{ height }}
      className="absolute inset-x-0 bottom-0 z-20 flex flex-col overflow-hidden rounded-t-xl border-x border-t border-primary/30 bg-white shadow-2xl"
    >
      {/* 높이 조절 핸들 */}
      <DragHandle onResize={onResize} />

      {/* 헤더 (고정) */}
      <div className="flex shrink-0 items-center justify-between bg-primary px-4 py-2.5 text-primary-foreground">
        <div className="flex items-center gap-3 text-sm font-semibold">
          {title}
          <span className="font-normal text-white/80">
            소계 {formatWon(subtotal)}원
          </span>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 transition-colors hover:bg-white/20"
          aria-label="닫기"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {hasData ? (
        <>
          {/* 대공사 탭바 (밑줄 라인형) */}
          <div className="flex shrink-0 flex-wrap [&>*+*]:border-l [&>*+*]:border-l-slate-200 border-b border-slate-200 bg-white px-3">
            {majors.map((major) => (
              <ExcelTab
                key={major.code}
                label={`${major.code} ${major.name}`}
                amount={majorWorkTotal(major)}
                active={columns.some((c) => c.code === major.code)}
                pinned={pinnedMajors.includes(major.code)}
                onSelect={() => selectMajor(major.code)}
                onTogglePin={() => togglePinMajor(major.code)}
              />
            ))}
          </div>

          {/* 대공사 컬럼 (최대 2단, 가로 분할) */}
          <div className="flex min-h-0 flex-1 gap-3 bg-slate-50 p-3">
            {columns.length === 0 ? (
              <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
                위 탭에서 대공사를 선택하세요.
              </div>
            ) : (
              columns.map((major) => {
                const sub = subState.get(major.code) ?? defaultSub(major);
                const showHeader =
                  columns.length > 1 || pinnedMajors.includes(major.code);
                return (
                  <MajorColumn
                    key={major.code}
                    major={major}
                    sub={sub}
                    showHeader={showHeader}
                    onSelectSub={(code) => selectSub(major, code)}
                    onTogglePinSub={(code) => togglePinSub(major, code)}
                    onEditItem={
                      onEditItem
                        ? (midCode, itemId, patch) =>
                            onEditItem(major.code, midCode, itemId, patch)
                        : undefined
                    }
                    onDeleteItem={
                      onDeleteItem
                        ? (midCode, itemId) =>
                            onDeleteItem(major.code, midCode, itemId)
                        : undefined
                    }
                    isItemDirty={
                      isItemDirty
                        ? (midCode, itemId, field) =>
                            isItemDirty(major.code, midCode, itemId, field)
                        : undefined
                    }
                  />
                );
              })
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center bg-slate-50 p-4">
          <div className="rounded-lg border border-dashed border-slate-300 bg-white px-8 py-10 text-center text-sm text-slate-400">
            아직 등록된 품목이 없습니다. 아래 버튼으로 품목을 추가하세요.
          </div>
        </div>
      )}

      {/* 하단 액션 바 (고정) */}
      <div className="flex shrink-0 flex-wrap gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Upload className="h-3.5 w-3.5" /> 엑셀 업로드
        </button>
        <button
          type="button"
          onClick={handleAddBlankItem}
          disabled={!onAddItems || !targetMid}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> 품목 직접 추가
        </button>
        <button
          type="button"
          onClick={() => setDbModalOpen(true)}
          disabled={!onAddItems || !targetMid}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Database className="h-3.5 w-3.5" /> DB 검색 추가
        </button>
      </div>

      <DbSearchModal
        open={dbModalOpen}
        onClose={() => setDbModalOpen(false)}
        kind={dbKind}
        onAdd={handleAddDbItems}
      />
    </div>
  );
}
